import { App } from '@capacitor/app';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { toast } from 'react-toastify';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';
import { destroyEcho } from '../services/echo';
import { initPushNotifications, resetPushInitialized } from '../services/push.service';
import { endWebPushForSession } from '../services/web-push.service';
import { consumeStashedReferralFlag, getStashedReferralCode } from '../services/referral.service';
import {
  clearStashedSignupSource,
  clearStashedSignupVariant,
  getStashedSignupSource,
  getStashedSignupVariant,
} from '../utils/signup-source';
import type { User } from '../types';
import type { ReplayOutcome as GuestReplayOutcome } from '../services/guestConversion.service';
import { isAccountBannedError } from '../types/errors';
import { clearRedditUser, identifyRedditUser } from '../utils/reddit-pixel';
import { trackSignupConversion } from '../utils/signup-conversion';
import { consumeGoogleAppNonce } from '../services/google-app-nonce';
import { applyAnalyticsIdentity } from '../utils/ga';
import { redactUrl } from '../utils/redact-url';
import { useSubscriptionStore } from './subscription.store';
import { useAccountBanStore, BANNED_FALLBACK } from './account-ban.store';
import { readCapabilities, setCapabilitiesSink } from './capabilities-sync';
import {
  type SessionRead,
  isCurrentSession,
  issueRead,
  notifySessionChange,
  resetUserScopedStores,
  beginSessionFlow,
  type SessionFlow,
  createReadLane,
  setSessionProvider,
  withOrderedSession,
  withSession,
} from './session-read';
import { getAxiosErrorMessage } from '../utils/error';

const API_URL = import.meta.env.VITE_API_URL;

/** The refusal a Google sign-in deep link this device did not start gets. */
export const GOOGLE_LINK_NOT_FOR_DEVICE = 'This sign-in link is not for this device';

// Type definitions
interface AppUrlOpenEvent {
  url: string;
}

/**
 * What registration reports about the verification email. The account is
 * created in every case; this says whether the user should change their
 * address, wait and retry, or simply check their inbox — three different
 * pieces of advice that all used to read as "check your inbox".
 */
export type VerificationEmailStatus = 'sent' | 'unreachable' | 'failed';

export interface RegisterResult {
  verification_email_status?: VerificationEmailStatus;
  [key: string]: unknown;
}

interface AuthState {
  isAuthenticated: boolean;
  isGoogleLoading: boolean;
  isLoading: boolean;
  isButtonLoading: boolean;
  token: string | null;
  user: User | null;
  /** Guest "tour" mode: no account, no token, no server row. Persisted so a reload
   * keeps the tour. Cleared on login/register/logout. See WS3 guest tour. */
  isGuest: boolean;
  _deepLinkListenersInitialized: boolean;
  _deepLinkListenerRef: PluginListenerHandle | null;
}

/** A sign-in's answer. `ended`: a ban for a session that ended meanwhile (c-057): show nothing. */
type SignInResult = { data?: unknown; error?: string; ended?: true };

interface AuthActions {
  setAuthState: (state: Partial<AuthState>) => void;
  initAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<SignInResult>;
  register: (
    name: string,
    username: string,
    email: string,
    password: string,
    data_collection_consent?: boolean,
    terms_accepted?: boolean,
    antiBot?: { intakeRef?: string; signupFormMs?: number },
  ) => Promise<{ data?: RegisterResult; error?: string }>;
  logout: () => Promise<{ success?: string; error?: string }>;
  /** Everything logout() does on this device, without the API call: for a
   *  session the server has already ended (a ban revoked the token). */
  clearSessionLocally: () => void;
  initializeDeepLinks: () => void;
  cleanupDeepLinks: () => void;
  setGoogleLoading: (loading: boolean) => void;
  handleGoogleCallback: (token: string, user: User, isNewUser?: boolean) => void;
  signInWithApple: (payload: {
    identityToken: string;
    authorizationCode?: string | null;
    givenName?: string | null;
    familyName?: string | null;
    email?: string | null;
  }) => Promise<SignInResult>;
  forgotPassword: (email: string) => Promise<{ data?: unknown; error?: string; message?: string }>;
  resetPassword: (
    email: string,
    token: string,
    password: string,
    password_confirmation: string,
  ) => Promise<{ data?: unknown; error?: string; message?: string }>;
  updateProfile: (profileData: {
    name?: string;
    current_password?: string;
    password?: string;
    password_confirmation?: string;
    social_links?: Record<string, string>;
  }) => Promise<{ data?: unknown; error?: string; message?: string; superseded?: true }>;
  /** Same contract as patchUser: true when written, false when refused. */
  updateConsent: (consents: Partial<User>, read: SessionRead) => boolean;
  /** Synchronously merge fields into the in-memory user — for state the server has
   *  already confirmed (e.g. deletion scheduled/cancelled), so the UI never depends
   *  on a fire-and-forget refreshUser() succeeding.
   *
   *  `read` is the ticket (`issueRead()`, session-read.ts) taken when the
   *  confirming request was SENT. Required, so no caller can forget it: a
   *  confirmation for a session that has since ended is another account's and
   *  is refused (#940).
   *
   *  Returns whether it wrote. On false the caller must skip everything else
   *  the answer would have caused (toast, close, callback, refresh). */
  patchUser: (fields: Partial<User>, read: SessionRead) => boolean;
  /** Resolves true when /me was actually reached, false when the check could not
   *  be made (offline, no token), and null when the answer belongs to a session
   *  that has since ended (#940): nothing was written and nothing should be
   *  reported. Most callers ignore it; the verify gate needs it to avoid
   *  reporting "still not verified" when nothing was checked. */
  refreshUser: () => Promise<boolean | null>;
  /** The /me half of refreshUser, without the plan read that follows it.
   *  'applied': written; 'superseded': a newer write landed first; 'ended': the
   *  session that asked has ended; 'failed': not reached (offline, no token). */
  refreshMe: () => Promise<'applied' | 'superseded' | 'ended' | 'failed'>;
  /** Enter guest "tour" mode (no account). */
  startGuest: () => void;
  /** Leave guest mode (e.g. on convert-to-account). */
  exitGuest: () => void;
  /** After a guest becomes authenticated (register, login, or OAuth), replay their
   *  locally-stored tour answers through the real authed quiz endpoints so the
   *  SERVER grades and records them. Fire-and-forget; call with the pre-transition
   *  isGuest value (setAuthState clears the flag). */
  /**
   * Replays a converted guest's tour quizzes. Resolves to what the replay did
   * (null when nothing ran: not a guest, nothing saved, or the session ended);
   * the sign-in paths fire it and forget, the learner hears the outcome as a
   * toast.
   */
  maybeReplayGuestProgress: (wasGuest: boolean) => Promise<GuestReplayOutcome | null>;
}

type AuthStore = AuthState & AuthActions;

/**
 * Generate headers for API requests with mobile app detection
 * Used for bootstrap calls that use fetch directly (login, register, etc.)
 */
const getApiHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Add mobile app identifier if running in Capacitor
  if (Capacitor.isNativePlatform()) {
    headers['X-App-Type'] = 'nvc-mobile-app';
    headers['User-Agent'] = `NVCApp/${Capacitor.getPlatform()} Capacitor`;
  }

  return headers;
};

/**
 * Ordering lane for writes to `user` (session-read.ts createReadLane). Each
 * /me takes a ticket; patchUser (server-confirmed local writes) supersedes
 * every /me in flight. A /me only applies if it is still the newest, so a
 * stale in-flight /me (e.g. the one fired on opening the delete modal) cannot
 * clobber state the server has since confirmed, such as deletion_requested_at
 * (Codex #711 P2), and the ordering covers its failures too (#940 r7).
 */
const userReads = createReadLane();

/**
 * Ticket of the read whose capability map is on screen (capabilities-sync.ts).
 * Separate from the userReads lane on purpose: a plan refresh landing while a /me is
 * in flight must supersede that /me's CAPABILITIES without throwing away the
 * rest of it — dropping the whole /me would hide, say, a just-verified email
 * from the verify gate.
 */
let capabilitiesAppliedSeq = 0;

/**
 * Sign-out takes the account out of the 1:1 empathy queue FIRST (David,
 * 2026-10-04), while its token still works: POST /logout revokes it, and a
 * leave sent after that is refused. A switch (A→B) needs no such step, since
 * A's token stays valid: the queue store's session-change listener leaves
 * under it. Having left here, the queue store is reset, so that listener finds
 * nothing left to leave when /logout then clears the session.
 *
 * Best-effort and bounded: the leave waits at most its own timeout
 * (SESSION_END_LEAVE_TIMEOUT_MS), the whole step never throws, and a sign-out
 * never hangs on it. Dynamic import, as for the other stores this one cannot
 * import statically (a cycle through api.ts); the queue store is in the entry
 * chunk (useAppBootstrap imports it), so the import never touches the network.
 */
async function leaveEmpathyQueueBeforeSignOut(
  flow: SessionFlow,
  token: string | null,
): Promise<void> {
  if (!token) return;
  try {
    const queue = await import('./empathy-queue.store');
    const left = await queue.leaveQueueForEndingSession(token);
    // Reset only after a CONFIRMED leave (Codex P1 on #945), and only for the
    // session that left. A failed leave keeps the queue state: if /logout then
    // fails too, the account is still signed in and still queued server-side,
    // so its screen and its polls must go on saying so. If /logout succeeds,
    // the session-change reset clears the store anyway.
    if (left && flow.isCurrent()) queue.useEmpathyQueueStore.getState().reset();
  } catch (error) {
    console.warn('[auth] could not leave the empathy queue before sign-out:', error);
  }
}

/**
 * The same first step for the account's live CIRCLE and its AI buddy session
 * (David, 2026-10-04), siblings of the queue leave above: each goes through
 * its store's own way out (`leaveCircleForEndingSession`,
 * `endAiBuddyForEndingSession`), under A's token, before /logout revokes it.
 * A switch needs no such step: each store's session-change listener ends its
 * session under A's still-valid token.
 *
 * After a CONFIRMED circle leave, and only for the session that left, the
 * circle store shows what the session's own Leave shows (polling stopped,
 * out of the session): if /logout then fails, the account is still signed in
 * and must not look seated in a circle it left. If /logout succeeds, the
 * session-change reset clears the store anyway. A failed leave changes
 * nothing.
 *
 * Best-effort and bounded as a whole by SESSION_END_LEAVE_TIMEOUT_MS: these
 * stores live in lazy chunks, and a chunk the account never opened is fetched
 * by the import here, so the import is inside the bound too. Never throws.
 */
async function endCircleSessionsBeforeSignOut(
  flow: SessionFlow,
  token: string | null,
): Promise<void> {
  if (!token) return;
  try {
    // The queue store is in the entry chunk: the shared bound, without a fetch.
    const { SESSION_END_LEAVE_TIMEOUT_MS } = await import('./empathy-queue.store');
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = new Promise<'timeout'>((resolve) => {
      timer = setTimeout(() => resolve('timeout'), SESSION_END_LEAVE_TIMEOUT_MS);
    });
    const work = Promise.all([
      import('./circle-session.store').then(async (circle) => {
        const outcome = await circle.leaveCircleForEndingSession(token);
        if (outcome === 'left' && flow.isCurrent()) {
          circle.useCircleSessionStore.getState().stopPolling();
          circle.useCircleSessionStore.setState({ isInSession: false });
        }
      }),
      import('./ai-buddy-session.store').then((buddy) => buddy.endAiBuddyForEndingSession(token)),
    ]);
    // A failure that lands after the bound has nobody left to report to.
    work.catch(() => {});
    const settled = await Promise.race([work, timedOut]);
    clearTimeout(timer);
    if (settled === 'timeout') {
      console.warn('[auth] leaving the circle before sign-out timed out; signing out without it');
    }
  } catch (error) {
    console.warn('[auth] could not leave the circle before sign-out:', error);
  }
}

/**
 * Settle which capability map a whole-user write lands with.
 *
 * - A new session or a different account: the incoming user wins outright and
 *   supersedes every read issued before it, so a plan read still in flight for
 *   the previous account can never land on this one.
 * - Same account, and the payload carries a map: it wins if its read is newer
 *   than the map on screen (`readSeq` is the /me ticket; payloads that are not
 *   reads, like login, count as now).
 * - Otherwise keep the map on screen. Login and profile payloads carry none,
 *   and replacing the user wholesale used to blank it.
 */
function resolveIncomingUser(
  incoming: User,
  current: User | null,
  wasAuthenticated: boolean,
  readSeq?: number,
): User {
  if (!current || !wasAuthenticated || current.id !== incoming.id) {
    capabilitiesAppliedSeq = issueRead().seq;
    return incoming;
  }
  const carriesMap = readCapabilities(incoming.capabilities) !== null;
  if (carriesMap && (readSeq === undefined || readSeq > capabilitiesAppliedSeq)) {
    capabilitiesAppliedSeq = readSeq ?? issueRead().seq;
    return incoming;
  }
  return { ...incoming, capabilities: current.capabilities };
}

/**
 * Authentication store using Zustand
 * Manages authentication state and auth-related operations
 */
export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // =========================================
      // State
      // =========================================

      /**
       * Indicates if user is currently authenticated
       */
      isAuthenticated: false,

      /**
       * Indicates if Google OAuth is in progress
       */
      isGoogleLoading: false,

      /**
       * Tracks loading state during auth operations
       */
      isLoading: true,
      isButtonLoading: false,

      /**
       * Stores the authentication token
       */
      token: null,

      user: null,

      /**
       * Guest "tour" mode — no account/token. Persisted so a reload keeps the tour.
       */
      isGuest: false,

      /**
       * Track if deep link listeners are initialized
       */
      _deepLinkListenersInitialized: false,

      /**
       * Store reference to deep link listener for cleanup
       */
      _deepLinkListenerRef: null,

      // =========================================
      // Auth Actions
      // =========================================

      /**
       * Updates the authentication state
       * @param {Object} state - New authentication state
       */
      setAuthState: (state: Partial<AuthState>) => {
        // Any successful authentication SPENDS the pre-auth attribution hint
        // (Codex P2 on #823): a signed-in session's leftover stash must not
        // label whoever registers next in this browser. This is the one
        // chokepoint every login/register/OAuth success routes through, so
        // no individual path can forget it. Register reads the hint into its
        // payload BEFORE reaching here.
        if (state.isAuthenticated === true) clearStashedSignupSource();
        if (state.isAuthenticated === true) clearStashedSignupVariant();
        // An ABSENT field keeps its value; an explicit null CLEARS it (#940).
        // This used to be `state.x ?? get().x`, so `user: null` and
        // `token: null` silently kept the previous account's: sign-out left
        // A's user (and A's consent) in place for a guest or the next visitor.
        set({
          isAuthenticated: state.isAuthenticated ?? get().isAuthenticated,
          isLoading: state.isLoading ?? get().isLoading,
          token: state.token === undefined ? get().token : state.token,
          user:
            state.user === undefined
              ? get().user
              : state.user === null
                ? null
                : resolveIncomingUser(state.user, get().user, get().isAuthenticated),
          // Any real authentication ends guest mode — every login/register/OAuth
          // success routes through here with isAuthenticated:true.
          isGuest: state.isAuthenticated === true ? false : get().isGuest,
        });
      },

      /**
       * Initializes authentication state on app startup
       * Validates existing token if present
       */
      initAuth: async () => {
        try {
          const token = get().token;
          if (!token) {
            get().setAuthState({
              isAuthenticated: false,
              isLoading: false,
              user: null,
            });
            return;
          }

          if (Capacitor.isNativePlatform()) {
            await new Promise((res) => setTimeout(res, 300));
          }

          // Session-bound: if someone signed in while the persisted token was
          // being checked, this answer (or this failure) is about the previous
          // session. Leave theirs alone: no write, no sign-out.
          const outcome = await withSession(() => api.get('/me'));
          if (outcome.status === 'ended') return;
          if (outcome.status === 'failed') throw outcome.error;
          const { data }: { data?: User } = outcome.value.data;
          get().setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user: data ?? null,
          });

          // Fetch subscription data after successful authentication
          await useSubscriptionStore.getState().fetchSubscription();
        } catch (error) {
          console.error('Auth initialization failed:', error);
          get().setAuthState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
          });
        }
      },

      /**
       * Handles user login
       * Authenticates user with email and password
       * @param {string} email - User's email
       * @param {string} password - User's password
       * @returns {Promise<Object>} Response containing success data or error
       */
      login: async (email: string, password: string) => {
        // A ban answer is for the session this sign-in began under (c-057).
        const sentUnder = issueRead();
        try {
          const response = await fetch(`${API_URL}/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });

          const payload: { data?: { token: string; user: User }; error?: string } =
            await response.json();
          const { data, error } = payload;

          if (response.status === 403 && isAccountBannedError(payload)) {
            // Ignored for a session that ended meanwhile: A's "suspended"
            // message is not for whoever is on screen now.
            if (!useAccountBanStore.getState().suspend(payload, sentUnder)) return { ended: true };
            return { error: payload.message };
          }

          if (response.ok && data) {
            const wasGuest = get().isGuest; // captured before setAuthState clears it
            get().setAuthState({
              isAuthenticated: true,
              isLoading: false,
              token: data.token,
              user: data.user,
            });
            void get().maybeReplayGuestProgress(wasGuest);

            // Fetch subscription data
            await useSubscriptionStore.getState().fetchSubscription();

            // Initialize push notifications
            try {
              await initPushNotifications();
              console.log('Push notifications initialized successfully');
            } catch (pushError) {
              console.error('Failed to initialize push notifications:', pushError);
            }

            // Reddit pixel: identify user for Advanced Matching.
            // Awaited so the SHA-256 hashing completes and the new init is queued
            // before any subsequent track call can race ahead of it.
            // Wrapped in try/catch so a tracking-side failure (e.g., crypto.subtle
            // unavailable, privacy extension blocking) cannot break the auth flow.
            //
            // Gated on data_collection_consent for GDPR/CCPA compliance — we
            // hash the user's email and userId before sending, but that's still
            // user-identifying data leaving the device, so it requires consent.
            if (data.user.data_collection_consent) {
              try {
                await identifyRedditUser(email, data.user.id);
              } catch (err) {
                console.error('Failed to identify Reddit user:', err);
              }
            }

            return { data };
          }
          return { error };
        } catch (error) {
          return {
            error: error instanceof Error ? error.message : String(error),
          };
        }
      },

      register: async (
        name: string,
        username: string,
        email: string,
        password: string,
        data_collection_consent = false,
        terms_accepted = false,
        // Anti-bot signals from the form, as a trailing object rather than a
        // seventh and eighth positional arg. Both are optional: an older cached
        // build that omits them must still be able to register.
        antiBot: { intakeRef?: string; signupFormMs?: number } = {},
      ) => {
        try {
          // A referral code, if the user arrived via an invite link (?ref=CODE),
          // was stashed by the register page. Read it here so it rides along with
          // the signup, and clear it after so it can't attach to a later account.
          // Re-validate the format (matches server max:16): an out-of-format value
          // — from an older build or a crafted link — must never be sent, or the
          // backend's validation would reject the whole registration.
          const referralCode = getStashedReferralCode();
          // Guest-tour attribution: still true at this point (setAuthState clears
          // it only after success). Allowlist-sanitized server-side; analytics
          // only, so it can never block the signup.
          const fromGuest = get().isGuest;

          const response = await fetch(`${API_URL}/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name,
              username,
              email,
              password,
              data_collection_consent,
              terms_accepted,
              ...(referralCode ? { referral_code: referralCode } : {}),
              // guest_tour outranks a stashed URL source (the tour is the
              // in-product journey that converted them); a comic-referred
              // non-guest signup carries the stash instead. Same precedence
              // as buildOAuthState so the paths cannot disagree.
              ...(fromGuest
                ? { signup_source: 'guest_tour' }
                : getStashedSignupSource()
                  ? { signup_source: getStashedSignupSource() }
                  : {}),
              // The website A/B variant, if the visitor came through one.
              ...(getStashedSignupVariant() ? { signup_variant: getStashedSignupVariant() } : {}),
              // Honeypot: always sent, normally empty. A real browser submits
              // the empty hidden input; only a script that fills every field it
              // finds puts anything here.
              intake_ref: antiBot.intakeRef ?? '',
              ...(typeof antiBot.signupFormMs === 'number'
                ? { signup_form_ms: antiBot.signupFormMs }
                : {}),
            }),
          });

          const {
            data,
            error,
          }: { data?: { token: string; user: User } & RegisterResult; error?: string } =
            await response.json();

          if (response.ok && data) {
            // Signup succeeded — the referral (if any) is now attributed server-side.
            // Clear the stashed code so it can't attach to a future account on this device.
            try {
              localStorage.removeItem('referral_code');
            } catch {
              /* ignore */
            }
            clearStashedSignupSource();
            clearStashedSignupVariant(); // same one-account contract as the referral stash

            const wasGuest = get().isGuest; // captured before setAuthState clears it
            get().setAuthState({
              isAuthenticated: true,
              isLoading: false,
              token: data.token,
              user: data.user,
            });
            void get().maybeReplayGuestProgress(wasGuest);

            // Fetch subscription data
            await useSubscriptionStore.getState().fetchSubscription();

            // Initialize push notifications
            try {
              await initPushNotifications();
              console.log('Push notifications initialized successfully');
            } catch (pushError) {
              console.error('Failed to initialize push notifications:', pushError);
            }

            // Reddit pixel: identify FIRST so the SignUp event has Advanced Matching
            // applied (hashed email + externalId). Without this ordering, SignUp
            // fires with no match keys, defeating the whole point of Advanced
            // Matching on our most valuable conversion event. Awaited for the
            // same reason as the login path — SHA-256 hashing must complete and
            // the new init must be queued before track('SignUp') is called.
            // Wrapped in try/catch so a tracking-side failure cannot make a
            // successful registration appear as an error to the user.
            //
            // identifyRedditUser stays consent-gated: it attaches a hashed
            // email/externalId to the Reddit cookie (PII / Advanced Matching),
            // so it only runs for users who granted data_collection_consent.
            if (data.user.data_collection_consent) {
              try {
                await identifyRedditUser(email, data.user.id);
              } catch (err) {
                console.error('Failed to identify Reddit user:', err);
              }
            }
            // SignUp is our primary ad-optimization conversion. It is fired
            // via trackConversion (NOT the consent-gated trackUserEvent) so it
            // reaches Reddit for every registrant: the payload carries no PII
            // and identity correlation stays gated above, so this is an
            // anonymous-equivalent conversion count. Gating it behind the
            // optional consent checkbox (default false) previously meant
            // Reddit never received SignUp and could not optimize toward it.
            // See trackConversion's docblock for the full privacy rationale.
            //
            // Wrapped in try/catch alongside identifyRedditUser above: the
            // comment at line 300 promises tracking-side failures cannot
            // make a successful registration appear as an error to the user.
            trackSignupConversion('email', fromGuest, !!referralCode);

            return { data };
          }

          // A WAITLIST 423 from /register still CREATED the account (beta capacity
          // is internal gating, not a failed conversion) — count the signup so the
          // GA4 funnel matches the admin metric (Codex #712 P2 round 2), and
          // consume the attribution stash: it attached to THIS account and must
          // not label a later one (Codex P2 on #823 — the referral stash's #819
          // lesson, same branch).
          if (error === 'WAITLIST') {
            trackSignupConversion('email', fromGuest, !!referralCode);
            clearStashedSignupSource();
            clearStashedSignupVariant();
          }

          return { error };
        } catch (error) {
          return {
            error: error instanceof Error ? error.message : String(error),
          };
        }
      },

      /**
       * Handles user logout
       * Clears authentication state and token
       */
      logout: async () => {
        // The whole sign-out belongs to the session that asked for it (Codex
        // P1 on #945): the queue leave can take seconds, and a sign-in as
        // someone else meanwhile must not be revoked or cleared by A's tap.
        const flow = beginSessionFlow();
        const token = get().token;
        try {
          set({ isButtonLoading: true });
          // In parallel, so the sign-out waits for the slowest, not the sum.
          // This browser's push subscription goes too, while the token can
          // still delete its row (c-027): the next person on a shared
          // browser must not get this account's pushes.
          await Promise.all([
            leaveEmpathyQueueBeforeSignOut(flow, token),
            endCircleSessionsBeforeSignOut(flow, token),
            token ? endWebPushForSession(token, flow.isCurrent) : undefined,
          ]);
          if (!flow.isCurrent()) {
            return { error: 'The account changed before sign-out finished' };
          }
          resetPushInitialized();
          destroyEcho();
          await api.post('/logout');

          // A was signed out server-side; if B signed in meanwhile, B stays.
          if (flow.isCurrent()) get().clearSessionLocally();

          return { success: 'Logout successful' };
        } catch (_error) {
          return { error: 'Logout failed' };
        } finally {
          set({ isButtonLoading: false });
        }
      },

      clearSessionLocally: () => {
        // Idempotent: logout() already ran these two before its API call.
        resetPushInitialized();
        destroyEcho();

        // Clearing the token is what ends the session: the session-change
        // subscriber below then resets every per-account store and dismisses
        // every flow opened for this session (#940).
        get().setAuthState({
          isAuthenticated: false,
          isLoading: false,
          token: null,
          user: null,
        });
        set({ isGuest: false });

        // Analytics identity clears EXPLICITLY (Codex P1 on #816 round 2), so
        // post-logout events (and the next visitor on a shared device) are
        // never correlated to the previous user, whatever the providers
        // subscription makes of the transition.
        applyAnalyticsIdentity(null);

        // Reddit pixel: clear the Advanced Matching identifiers so events
        // fired after logout (PageVisit on redirect, etc.) don't remain
        // correlated to the user's previous hashed email/externalId. The
        // trackUserEvent consent checker will also start returning true
        // again (user is null = anonymous-allowed), so anonymous tracking
        // resumes from a clean identity.
        clearRedditUser();
      },

      /**
       * Initialize deep link listeners for mobile OAuth
       */
      initializeDeepLinks: () => {
        if (!Capacitor.isNativePlatform()) return;

        // Prevent multiple initializations
        if (get()._deepLinkListenersInitialized) return;

        // Track processed URLs to prevent duplicate processing
        const processedUrls = new Set();

        const handleAppUrlOpen = (event: AppUrlOpenEvent) => {
          try {
            const url = new URL(event.url);

            // Check if this URL has already been processed
            if (processedUrls.has(event.url)) return;

            // Add URL to processed set
            processedUrls.add(event.url);

            // Check if this is a Google OAuth callback
            if (url.protocol === 'nvcapp:' && url.pathname.includes('/auth/google/callback')) {
              const params = new URLSearchParams(url.search);

              // Only the sign-in this device started (login CSRF): any page or
              // message can open this link, with a token of the sender's
              // choosing. `an` must be the nonce minted before the system
              // browser opened, and it is spent here (services/google-app-nonce).
              if (!consumeGoogleAppNonce(params.get('an'))) {
                console.warn('[google-signin] deep link refused', {
                  reason: params.get('an') ? 'nonce_mismatch' : 'nonce_missing',
                });
                set({ isGoogleLoading: false });
                toast.error(GOOGLE_LINK_NOT_FOR_DEVICE);
                return;
              }

              // Banned: no token, just a notice to redeem for the suspended screen.
              if (params.get('error') === 'account_banned') {
                set({ isGoogleLoading: false });
                void useAccountBanStore
                  .getState()
                  .redeemNotice(params.get('notice') ?? '')
                  .then((outcome) => {
                    if (outcome === 'unavailable') toast.error(BANNED_FALLBACK);
                  });
                return;
              }

              const token = params.get('token');
              const userParam = params.get('user');

              if (token && userParam) {
                try {
                  const user = JSON.parse(decodeURIComponent(userParam));

                  const wasGuest = get().isGuest; // captured before the set clears it

                  // Capture-then-clear in one move (Codex P2 on #818; this
                  // path previously never cleared the stash at all).
                  const wasReferred = consumeStashedReferralFlag();
                  clearStashedSignupSource();
                  clearStashedSignupVariant(); // same one-account contract as the referral stash

                  // Directly update auth state. Real auth ends guest mode — this
                  // direct set() bypasses setAuthState's clearing (Codex #709 P2).
                  set({
                    isAuthenticated: true,
                    isGuest: false,
                    isLoading: false,
                    isGoogleLoading: false,
                    token: token,
                    user: user,
                  });
                  void get().maybeReplayGuestProgress(wasGuest);

                  // Same contract as handleGoogleCallback: the backend appends
                  // is_new=1 only when this OAuth pass created the account.
                  if (params.get('is_new') === '1')
                    trackSignupConversion('google', wasGuest, wasReferred);

                  api.defaults.headers.common.Authorization = `Bearer ${token}`;
                  // No "now logged in" toast: the page change says it worked
                  // (David, 2026-10-29; the web callback page dropped its own).
                } catch (parseError) {
                  console.error('Error parsing mobile OAuth user data:', parseError);
                  set({ isGoogleLoading: false });

                  setTimeout(() => {
                    toast.error('Login failed. Invalid user data.');
                    window.location.href = '/login?error=oauth_data_error';
                  }, 100);
                }
              } else {
                console.error('Missing token or user data in mobile callback');
                set({ isGoogleLoading: false });

                setTimeout(() => {
                  toast.error('Login failed. Missing authentication data.');
                  window.location.href = '/login?error=oauth_missing_data';
                }, 100);
              }
            }
          } catch (urlError) {
            console.error('Error parsing deep link URL:', urlError);
            set({ isGoogleLoading: false });
          }
        };

        // Store listener reference for cleanup
        void App.addListener('appUrlOpen', handleAppUrlOpen).then((listenerRef) => {
          // Mark as initialized
          set({
            _deepLinkListenersInitialized: true,
            _deepLinkListenerRef: listenerRef,
          });
        });

        // Check if app was opened with a URL
        void App.getLaunchUrl().then((launchUrl) => {
          if (launchUrl?.url) {
            console.log('App launched with URL:', redactUrl(launchUrl.url));

            // Add launch URL to processed set before calling handler
            processedUrls.add(launchUrl.url);
            handleAppUrlOpen({ url: launchUrl.url });
          }
        });
      },

      /**
       * Cleanup deep link listeners
       */
      cleanupDeepLinks: () => {
        const listenerRef = get()._deepLinkListenerRef;
        if (listenerRef) {
          void listenerRef.remove();
          set({
            _deepLinkListenersInitialized: false,
            _deepLinkListenerRef: null,
          });
          console.log('Deep link listeners cleaned up');
        }
      },

      /**
       * Set Google OAuth loading state
       * @param {boolean} loading - Loading state
       */
      setGoogleLoading: (loading: boolean) => {
        set({ isGoogleLoading: loading });

        // Failsafe: Reset loading state after timeout (mobile backup)
        if (loading && Capacitor.isNativePlatform()) {
          setTimeout(() => {
            const currentState = get();
            if (currentState.isGoogleLoading && !currentState.isAuthenticated) {
              console.log('Mobile OAuth timeout - resetting loading state');
              set({ isGoogleLoading: false });

              // Import toast dynamically to avoid circular imports
              toast.error('Google login timed out. Please try again.');
            }
          }, 30000); // 30 second timeout
        }
      },

      /**
       * Handle Google OAuth callback
       * @param {string} token - JWT token from Google OAuth
       * @param {Object} user - User object from Google OAuth
       */
      handleGoogleCallback: (token: string, user: User, isNewUser = false) => {
        console.log('Google callback - setting auth state:', {
          token: !!token,
          user: user?.name,
        });

        // Capture-then-clear in one move (Codex P2 on #818 — the clear used
        // to run first, so every referred OAuth signup reported false). Social
        // signup doesn't attribute the code server-side yet (v1 limitation —
        // see register.tsx); clearing stops it mis-attaching to a later email
        // signup on a shared device.
        const wasReferred = consumeStashedReferralFlag();
        // The one direct-set door the setAuthState chokepoint cannot cover
        // (Codex #823 r4): spend the hint here too, same one-account contract.
        clearStashedSignupSource();
        clearStashedSignupVariant();

        const wasGuest = get().isGuest; // captured before the set clears it

        // Use direct set to ensure immediate state update
        set({
          isAuthenticated: true,
          // Real auth ends guest mode — this direct set() bypasses setAuthState's
          // clearing (Codex #709 P2).
          isGuest: false,
          isLoading: false,
          isGoogleLoading: false, // Reset Google loading state
          token: token,
          user: user,
        });
        void get().maybeReplayGuestProgress(wasGuest);

        // is_new comes from the backend callback URL — only the server knows
        // whether this OAuth pass CREATED the account (Codex #712 P2: without
        // this, social conversions were invisible to the GA4 funnel).
        if (isNewUser) trackSignupConversion('google', wasGuest, wasReferred);

        console.log('Google callback - auth state set, isAuthenticated:', get().isAuthenticated);

        // Force a re-render by updating a dummy state
        set((state) => ({ ...state }));
      },

      /**
       * Sign in with Apple (Apple Review Guideline 4.8).
       *
       * The native iOS flow (@capacitor-community/apple-sign-in) returns an
       * identity token (plus name/email on first authorization). We POST it to
       * the backend, which verifies the token against Apple's JWKS and returns
       * a Sanctum token + user — the same shape the email/password login uses,
       * so we set auth state identically.
       *
       * @returns { data } on success or { error } with a user-facing message.
       */
      signInWithApple: async (payload: {
        identityToken: string;
        authorizationCode?: string | null;
        givenName?: string | null;
        familyName?: string | null;
        email?: string | null;
      }) => {
        // A ban answer is for the session this sign-in began under (c-057).
        const sentUnder = issueRead();
        try {
          const response = await fetch(`${API_URL}/auth/apple`, {
            method: 'POST',
            headers: getApiHeaders(),
            body: JSON.stringify({
              identity_token: payload.identityToken,
              code: payload.authorizationCode ?? undefined,
              given_name: payload.givenName ?? undefined,
              family_name: payload.familyName ?? undefined,
              email: payload.email ?? undefined,
              // Attributed server-side ONLY if this sign-in creates a new account.
              referral_code: getStashedReferralCode() ?? undefined,
              // Guest-tour attribution — same new-accounts-only contract;
              // otherwise a stashed URL source (storyforge) rides along.
              signup_source: get().isGuest ? 'guest_tour' : (getStashedSignupSource() ?? undefined),
              signup_variant: getStashedSignupVariant() ?? undefined,
            }),
          });

          const body: {
            data?: { token: string; user: User; is_new_user?: boolean };
            error?: string;
          } = await response.json();
          const { data, error } = body;

          if (response.status === 403 && isAccountBannedError(body)) {
            // Ignored for a session that ended meanwhile: nothing to toast.
            if (!useAccountBanStore.getState().suspend(body, sentUnder)) return { ended: true };
            return { error: body.message };
          }

          if (response.ok && data) {
            // Same as the Google path: social signup doesn't attribute the referral
            // code yet (v1 limitation). Capture-then-clear in one move (Codex
            // P2 on #818 — clearing first made every referred signup report
            // false).
            const wasReferred = consumeStashedReferralFlag();
            clearStashedSignupSource();
            clearStashedSignupVariant(); // same one-account contract as the referral stash

            const wasGuest = get().isGuest; // captured before setAuthState clears it
            get().setAuthState({
              isAuthenticated: true,
              isLoading: false,
              token: data.token,
              user: data.user,
            });
            void get().maybeReplayGuestProgress(wasGuest);

            // Backend sets is_new_user only when this sign-in CREATED the account
            // (Codex #712 P2: social conversions were invisible to the GA4 funnel).
            if (data.is_new_user === true) trackSignupConversion('apple', wasGuest, wasReferred);

            // Fetch subscription data
            await useSubscriptionStore.getState().fetchSubscription();

            // Initialize push notifications (best-effort; mirror login())
            try {
              await initPushNotifications();
            } catch (pushError) {
              console.error('Failed to initialize push notifications:', pushError);
            }

            return { data };
          }

          // The WAITLIST 423 also carries is_new_user: a waitlisted CREATION is
          // still a signup — count it so the GA4 funnel matches the admin metric
          // (a returning waitlisted sign-in reports false and stays silent).
          // Consume the stash here too (Codex P2 on #819): this branch used to
          // leave the code behind, where it could mis-attach to a later email
          // signup on the same device.
          if (data?.is_new_user === true) {
            trackSignupConversion('apple', get().isGuest, consumeStashedReferralFlag());
            clearStashedSignupSource();
            clearStashedSignupVariant(); // same one-account contract
          }

          return { error: error || 'Apple sign-in failed' };
        } catch (error) {
          return {
            error: error instanceof Error ? error.message : String(error),
          };
        }
      },

      /**
       * Handle forgot password request
       * @param {string} email - User's email address
       * @returns {Promise<Object>} Response containing success data or error
       */
      forgotPassword: async (email: string) => {
        try {
          const response = await fetch(`${API_URL}/forgot-password`, {
            method: 'POST',
            headers: getApiHeaders(),
            body: JSON.stringify({ email }),
          });

          const { data, error, message }: { data?: unknown; error?: string; message?: string } =
            await response.json();

          if (response.ok) {
            return { data, message };
          }
          return { error };
        } catch (error) {
          return {
            error: error instanceof Error ? error.message : String(error),
          };
        }
      },

      /**
       * Handle password reset with token
       * @param {string} email - User's email address
       * @param {string} token - Reset token
       * @param {string} password - New password
       * @param {string} password_confirmation - Password confirmation
       * @returns {Promise<Object>} Response containing success data or error
       */
      resetPassword: async (
        email: string,
        token: string,
        password: string,
        password_confirmation: string,
      ) => {
        try {
          const response = await fetch(`${API_URL}/reset-password`, {
            method: 'POST',
            headers: getApiHeaders(),
            body: JSON.stringify({
              email,
              token,
              password,
              password_confirmation,
            }),
          });

          const { data, error, message }: { data?: unknown; error?: string; message?: string } =
            await response.json();

          if (response.ok) {
            return { data, message };
          }
          return { error };
        } catch (error) {
          return {
            error: error instanceof Error ? error.message : String(error),
          };
        }
      },

      /**
       * Update user profile (name and/or password)
       * @param {Object} profileData - Profile update data
       * @param {string} profileData.name - New name (optional)
       * @param {string} profileData.current_password - Current password (required for password change)
       * @param {string} profileData.password - New password (optional)
       * @param {string} profileData.password_confirmation - Password confirmation (required if password provided)
       * @returns {Promise<Object>} Response containing success data or error
       */
      updateProfile: async (profileData: {
        name?: string;
        current_password?: string;
        password?: string;
        password_confirmation?: string;
        social_links?: Record<string, string>;
      }) => {
        try {
          set({ isButtonLoading: true });

          // Session-bound: a save answered (or refused) for a session that has
          // since ended is another account's: superseded, nothing written or
          // reported (setAuthState would take a different id as an account
          // switch and install it).
          const outcome = await withSession(() => api.put('/profile', profileData));
          if (outcome.status === 'ended') return { superseded: true };
          if (outcome.status === 'failed') throw outcome.error;
          const { data, message }: { data?: { user: User }; message?: string } = outcome.value.data;
          get().setAuthState({
            user: data?.user,
          });
          return { data, message };
        } catch (error) {
          // The server's words in the shared order (field errors, then the
          // message, then the reason), else this sentence (backlog c-185).
          return { error: getAxiosErrorMessage(error, 'Failed to update profile') };
        } finally {
          set({ isButtonLoading: false });
        }
      },

      /**
       * Update user consent settings
       * @param {Object} consents - Consent settings to update
       * @returns {void}
       */
      updateConsent: (consents: Partial<User>, read: SessionRead) => {
        return get().patchUser(consents, read);
      },

      patchUser: (fields: Partial<User>, read: SessionRead) => {
        const currentUser = get().user;
        if (!currentUser || !isCurrentSession(read)) return false;
        // This is server-confirmed state: supersede any /me still in flight
        // so a stale response can't overwrite it (see userReads).
        userReads.supersede();
        set({
          user: {
            ...currentUser,
            ...fields,
          },
        });
        return true;
      },

      /**
       * Refresh user data from the server
       * Used after subscription changes to get updated user/subscription info
       * @returns {Promise<void>}
       */
      refreshMe: async () => {
        try {
          const token = get().token;
          if (!token) return 'failed';

          // Session-bound AND ordered (withOrderedSession): an answer OR a
          // failure for a session that has since ended is 'ended' (Codex r6),
          // and one a newer refresh or a confirmed write (patchUser) has
          // superseded writes nothing. A superseded answer still means /me was
          // reached; a superseded failure means it was not.
          const outcome = await withOrderedSession(userReads, () => api.get('/me'));
          if (outcome.status === 'ended') return 'ended';
          if (outcome.status === 'superseded') {
            return outcome.settled === 'resolved' ? 'superseded' : 'failed';
          }
          if (outcome.status === 'failed') return 'failed';
          const { read } = outcome;
          const response = outcome.value;
          const { data }: { data?: User } = response.data;
          set({
            user: data
              ? resolveIncomingUser(data, get().user, get().isAuthenticated, read.seq)
              : data,
          });
          return 'applied';
        } catch (_error) {
          return 'failed';
        }
      },

      refreshUser: async () => {
        // The boolean answers ONE question: did /me reach the server? The verify
        // gate needs to tell "we asked and the answer was no" apart from "we
        // never got to ask", so that it does not tell an offline user they are
        // still unverified when nothing was checked.
        const me = await get().refreshMe();
        if (me === 'failed') return false;
        if (me === 'ended') return null;
        if (me === 'superseded') return true;

        // Subscription is a SEPARATE concern, in its own try. Sharing the catch
        // meant a failing fetchSubscription() reported the whole refresh as
        // unreachable — even though /me had already succeeded and updated the
        // user — so the gate hid a real "still not verified" behind "you may be
        // offline" (Codex #765). Silent by design: the webhook reconciles.
        try {
          await useSubscriptionStore.getState().fetchSubscription();
        } catch (_error) {
          // Deliberately ignored — see above.
        }

        return true;
      },

      startGuest: () => {
        set({ isGuest: true, isLoading: false });
        // Defense-in-depth with the logout-time clear: guest surfaces must never
        // render a previous account's data (Codex #709). Synchronous, so it
        // cannot land after the guest's own data (Codex r9 on #940).
        resetUserScopedStores();
      },
      exitGuest: () => set({ isGuest: false }),

      maybeReplayGuestProgress: async (wasGuest: boolean) => {
        if (!wasGuest) return null;
        // Captured at the START, before the first await (#940 r7): the replay,
        // its toasts and its refreshes all belong to the account that just
        // signed in, and stop if that session ends.
        const flow = beginSessionFlow();
        // Dynamic imports: guest-conversion touches domain/progress stores, which
        // import this store — importing them statically here would be a cycle.
        const { useGuestStore } = await import('./guest.store');
        const pending = Object.keys(useGuestStore.getState().quizResults).length;
        if (pending === 0) return null;

        let outcome: GuestReplayOutcome;
        try {
          const { replayGuestProgress } = await import('../services/guestConversion.service');
          outcome = await replayGuestProgress(flow);
        } catch (error) {
          // The service catches each quiz's POST, so this is the chunk failing
          // to load: nothing was replayed, and the learner still has to know.
          console.error('Guest replay could not run:', error);
          outcome = { replayed: 0, failed: pending };
        }
        if (!flow.isCurrent()) return null;

        if (outcome.replayed > 0) {
          toast.success(
            `Your tour progress is saved — ${outcome.replayed} ${outcome.replayed === 1 ? 'quiz' : 'quizzes'} recorded.`,
          );
          // Server-side records changed: refresh everything derived from them.
          const { useDomainStore } = await import('./domain.store');
          useDomainStore.getState().invalidateCache();
          const { useProgressStore } = await import('./progress.store');
          useProgressStore.getState().setProgress(null);
          void get().refreshUser();
        }
        if (outcome.failed > 0) {
          // A warning, not info (c-050): it asks the learner to retake quizzes,
          // and a converted guest meets the onboarding welcome step right now.
          // The tour drops info and dismisses info already up (toastConfig.ts),
          // so it has to be a type the tour lets through, and it stays until
          // tapped so it outlives the step it may first appear under.
          toast.warning("Some tour progress couldn't be saved — you can retake those quizzes.", {
            autoClose: false,
          });
        }
        return outcome;
      },
    }),
    {
      /**
       * Persist configuration
       * Stores only the token in localStorage
       */
      name: 'auth-store',
      partialize: (state) => ({ token: state.token, isGuest: state.isGuest }),
    },
  ),
);

/**
 * The plan refresh's capability map lands here (capabilities-sync.ts). Applied
 * only to a signed-in user, and only if read after the map already on screen.
 * Deliberately NOT patchUser: that supersedes the userReads lane and would discard an
 * in-flight /me wholesale.
 */
/** A read's session is the bearer token it went out under (session-read.ts). */
setSessionProvider(() => useAuthStore.getState().token);

/**
 * THE session-change path (#940). Fires on every change of the bearer token
 * (null→A sign-in, A→B switch, A→null sign-out), whatever setter caused it:
 * setAuthState, the Google OAuth doors' bare set(), the 401 handler, the ban
 * teardown, or one nobody has written yet.
 *
 * 1. Every per-account store starts empty: the subscription store (which also
 *    clears the loading flag of a read the old session abandoned; that read
 *    comes back 'ended' and writes nothing, so it cannot override the new
 *    session's) and every per-user store, synchronously (registerUserScopedReset).
 * 2. A new session's own reads start at once: its plan and its user. This
 *    replaces app.tsx's re-read on "token became present", which a direct
 *    A→B switch never triggered.
 */
useAuthStore.subscribe((state, prev) => {
  if (state.token === prev.token) return;
  // Flows opened for the old session (agreement sheet and its parked
  // requests, upgrade and trial prompts, celebrations, the empathy feedback
  // form, ...) are dismissed or declined before anything else can act on them.
  notifySessionChange(state.token, prev.token);
  // Synchronous, at the switch (Codex r9): each per-user store (the
  // subscription among them) registered its own reset at load
  // (session-read.ts), so nothing can land after the new session's own writes.
  resetUserScopedStores();
  if (!state.token) return;
  void useSubscriptionStore.getState().fetchSubscription();
  void useAuthStore.getState().refreshMe();
});

setCapabilitiesSink((capabilities, readSeq) => {
  const { user, isAuthenticated } = useAuthStore.getState();
  if (!user || !isAuthenticated || readSeq <= capabilitiesAppliedSeq) return;
  capabilitiesAppliedSeq = readSeq;
  useAuthStore.setState({ user: { ...user, capabilities } });
});
